// VideoStyle.js
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Icon, Modal, Button } from 'antd'
import AspectRatioSelector from './components/AspectRatioSelector'
import StyleCard from '../StyleCard/StyleCard'
import CinematicIcon from '../../../../assets/video-cinematic.png'
import AnimeIcon from '../../../../assets/video-anime.png'
import ComicBookIcon from '../../../../assets/video-comic.png'
import OrigamiIcon from '../../../../assets/video-origami.png'
import DisneyIcon from '../../../../assets/video-disney.png'
import RealisticIcon from '../../../../assets/video-realistic.png'
import AnimatedIcon from '../../../../assets/video-3d-animated.png'

import './VideoStyle.scss'
import StepTitle from '../StepTitle'
import ShotGenerationStyle from './components/ShotGenerationStyle'

const videoStyles = [
  {
    key: 'realistic',
    title: 'Realistic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: RealisticIcon
  },
  {
    key: 'cinematic black & white',
    title: 'Cinematic',
    description: 'Photorealistic style with natural lighting and textures',
    icon: CinematicIcon
  },
  {
    key: 'comic book',
    title: 'Comic Book',
    description: 'Bold colors, strong outlines, and dramatic shading',
    icon: ComicBookIcon
  },
  {
    key: 'anime',
    title: 'Anime',
    description: 'Japanese animation style with vibrant colors',
    icon: AnimeIcon
  },
  {
    key: 'origami',
    title: 'Origami',
    description: 'Futuristic cyberpunk aesthetic with neon elements',
    icon: OrigamiIcon
  },
  {
    key: 'disney cartoon',
    title: 'Cartoon',
    description: 'Classic Disney characters with vibrant colors',
    icon: DisneyIcon
  },
  {
    key: '3d animated',
    title: '3D Animated',
    description: '3D animated style with realistic lighting and textures',
    icon: AnimatedIcon
  }
]
const VideoStyle = ({
  setFormData,
  formData,
  onValidationChange,
  onStyleChange,
  isDraftMode = false,
  videoId
}) => {
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [showStyleChangeModal, setShowStyleChangeModal] = useState(false)
  const [pendingStyleKey, setPendingStyleKey] = useState(null)

  // Shot Generation Style Logic
  const [showShotStyleChangeModal, setShowShotStyleChangeModal] = useState(
    false
  )
  const [pendingShotStyle, setPendingShotStyle] = useState(null)

  const isValid = useMemo(() => {
    return (
      Boolean(formData.aspectRatio) &&
      Boolean(formData.videoStyle && formData.videoStyle.key)
    )
  }, [formData.aspectRatio, formData.videoStyle])

  useEffect(() => {
    onValidationChange(isValid)
  }, [isValid])

  // Sync internal state with formData changes (e.g. after cloning or loading new video)
  useEffect(() => {
    if (formData.videoStyle && formData.videoStyle.key) {
      // Check if currently selected style differs from the one in formData
      if (selectedStyle !== formData.videoStyle.key) {
        setSelectedStyle(null) // Reset local override so it falls back to formData
      }
    }
  }, [formData.videoStyle])

  const hasGeneratedContent =
    formData.shotList &&
    formData.shotList.length > 0 &&
    formData.shotList.some(scene => scene.shots.some(shot => shot.imageSrc))

  const handleStyleSelect = key => {
    const selectedStyleObj = videoStyles.find(style => style.key === key)
    const currentStyleKey = formData.videoStyle && formData.videoStyle.key

    // Check if style is actually changing and if we have generated content
    if (
      currentStyleKey &&
      currentStyleKey !== key &&
      hasGeneratedContent &&
      onStyleChange
    ) {
      // Style is changing and we have content, show modal
      setPendingStyleKey(key)
      setShowStyleChangeModal(true)
    } else {
      // No content yet or same style, just update directly
      setSelectedStyle(key)
      setFormData(prev => ({
        ...prev,
        videoStyle: selectedStyleObj
      }))
    }
  }

  const handleClone = () => {
    Modal.confirm({
      title: 'Confirm Clone',
      content: 'Are you sure you want to create a new copy of this video?',
      centered: true,
      onOk: () => {
        const selectedStyleObj = videoStyles.find(
          style => style.key === pendingStyleKey
        )
        if (onStyleChange && selectedStyleObj) {
          onStyleChange(selectedStyleObj, true) // true = clone
        }
        setShowStyleChangeModal(false)
        setPendingStyleKey(null)
      }
    })
  }

  const handleOverride = () => {
    Modal.confirm({
      title: 'Confirm Override',
      content:
        'This will regenerate all images. Are you sure you want to continue?',
      centered: true,
      okType: 'danger',
      onOk: () => {
        const selectedStyleObj = videoStyles.find(
          style => style.key === pendingStyleKey
        )
        if (onStyleChange && selectedStyleObj) {
          onStyleChange(selectedStyleObj, false) // false = override
        } else if (selectedStyleObj) {
          // Fallback to direct update if handler not provided
          setSelectedStyle(pendingStyleKey)
          setFormData(prev => ({
            ...prev,
            videoStyle: selectedStyleObj
          }))
        }
        setShowStyleChangeModal(false)
        setPendingStyleKey(null)
      }
    })
  }

  const handleCancelStyleChange = () => {
    setShowStyleChangeModal(false)
    setPendingStyleKey(null)
  }

  const getPendingStyleName = () => {
    const style = videoStyles.find(s => s.key === pendingStyleKey)
    return style ? style.title : pendingStyleKey
  }

  // --- SHOT GENERATION STYLE HANDLERS ---

  const handleShotStyleSelect = style => {
    // Check if style is actually changing and if we have generated content
    const currentStyleId =
      formData.shotGenerationStyle && formData.shotGenerationStyle.id
    const newStyleId = style && style.id

    if (formData.shotList?.length > 0 && onStyleChange) {
      // Style is changing and we have content, show modal
      setPendingShotStyle(style)
      setShowShotStyleChangeModal(true)
    } else {
      // No content yet or same style, just update directly
      setFormData(prev => ({
        ...prev,
        shotGenerationStyle: style
      }))
    }
  }

  const handleShotClone = () => {
    if (onStyleChange && pendingShotStyle) {
      // We reuse the onStyleChange prop but pass a special flag or object to indicate it's a shot style change
      // However, the props from parent (VideoAi.js -> handleStyleChange) might be expecting a 'videoStyle' object or boolean.
      // Based on typical patterns, we might need a separate handler or overload the existing one.
      // Since the request says "allow them to clone or override", we should assume similar mechanism.
      // Let's pass a specific type to onStyleChange if needed, or better, stick to the pattern:
      // onStyleChange(newStyle, isClone, type) if the parent supports it.
      // Checking VideoAi.js would be ideal but based on context, let's try to adapt.
      // For now, I'll assume onStyleChange can handle this if I pass the new style and clone flag.

      // Actually, looking at the user request: "once a style is selected, it should add the data in formData... and also in ... send 'editingStyle' key value"
      // This suggests we might need to handle the cloning/overriding specifically for shot styles.
      // If the parent handler is generic:
      onStyleChange(pendingShotStyle, true, 'shot_style')
    }
    setShowShotStyleChangeModal(false)
    setPendingShotStyle(null)
  }

  const handleShotOverride = () => {
    if (onStyleChange && pendingShotStyle) {
      onStyleChange(pendingShotStyle, false, 'shot_style') // false = override
    } else {
      // Fallback
      setFormData(prev => ({
        ...prev,
        shotGenerationStyle: pendingShotStyle,
        editingStyle: true // Set flag as requested
      }))
    }
    setShowShotStyleChangeModal(false)
    setPendingShotStyle(null)
  }

  const handleCancelShotStyleChange = () => {
    setShowShotStyleChangeModal(false)
    setPendingShotStyle(null)
  }

  const getPendingShotStyleName = () => {
    return pendingShotStyle ? pendingShotStyle.title : 'Selected Style'
  }

  const handleRatioChange = ratio => {
    setFormData(prev => ({
      ...prev,
      aspectRatio: ratio
    }))
  }

  const shotGenerationStyleRef = useRef(null)

  return (
    <div className="visual-setup-container">
      {!isDraftMode && (
        <StepTitle
          iconType="camera"
          title="Visual Setup"
          description="Define your video's canvas format and artistic direction."
        />
      )}

      <div className="setup-sections-wrapper">
        <div className="setup-sections">
          {/* Section 01: Aspect Ratio */}
          <div className="setup-section">
            <div className="section-header-simple">
              <div className="header-left">
                {!isDraftMode && <span className="number-dot">1</span>}
                <h3>Canvas aspect ratio</h3>
              </div>
            </div>
            <AspectRatioSelector
              value={formData.aspectRatio || '16:9'}
              onChange={handleRatioChange}
            />
          </div>

          {/* Section 02: Artistic Style */}
          <div className="setup-section style-section">
            <div className="section-header-simple">
              <div className="header-left">
                {!isDraftMode && <span className="number-dot">2</span>}
                <h3>Video artistic style</h3>
              </div>
            </div>

            {!formData.videoStyle?.key && (
              <div className="selection-reminder">
                <Icon type="info-circle" />
                <span>Please select a style to proceed</span>
              </div>
            )}

            <div className="shot-list-cards">
              {videoStyles.map(style => (
                <StyleCard
                  key={style.key}
                  style={{
                    ...style,
                    thumbnailImage: style.icon,
                    isSystem: true
                  }}
                  isSelected={
                    selectedStyle === style.key ||
                    (formData.videoStyle &&
                      formData.videoStyle.key === style.key)
                  }
                  onSelect={() => handleStyleSelect(style.key)}
                  className={
                    style.key === '3d animated'
                      ? 'animated-3d-style-card'
                      : style.key === 'cinematic black & white'
                      ? 'cinematic-style-card'
                      : ''
                  }
                />
              ))}
            </div>
          </div>

          {/* Section 03: Shot Generation Style */}
          <div className="setup-section style-section">
            <div className="section-header-simple">
              <div className="header-left">
                {!isDraftMode && <span className="number-dot">3</span>}
                <h3>Editing Styles</h3>
              </div>
              <Button
                ghost
                type="primary"
                icon="plus"
                onClick={() =>
                  shotGenerationStyleRef.current?.openCreateStyleModal()
                }
              >
                Add New Style
              </Button>
            </div>

            <ShotGenerationStyle
              ref={shotGenerationStyleRef}
              formData={formData}
              setFormData={setFormData}
              onStyleSelect={handleShotStyleSelect}
              isDraftMode={isDraftMode}
              videoId={videoId}
            />
          </div>
        </div>
      </div>

      {/* Style Change Confirmation Modal */}
      <Modal
        visible={showStyleChangeModal}
        onCancel={handleCancelStyleChange}
        footer={null}
        centered
        width={520}
        className="video-style-change-modal"
      >
        <div className="video-style-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Existing Generated Content Detected</h3>
          <p className="modal-description">
            You have already generated images for this video. Changing the style
            to <strong>"{getPendingStyleName()}"</strong> conflicts with your
            existing work.
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>
          <div className="modal-options">
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video (Recommended)
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  style. Your current video and its images will be saved and
                  remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <div className="option-description">
                  Updates this exact video.
                  <div
                    style={{
                      border: '1px solid #ffa39e',
                      backgroundColor: '#fff1f0',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'rgba(0, 0, 0, 0.65)'
                    }}
                  >
                    <Icon
                      type="warning"
                      theme="filled"
                      style={{ color: '#f5222d', marginRight: '6px' }}
                    />
                    <strong>Warning:</strong> All your existing images will be
                    deleted and regenerated to match the new style. This cannot
                    be undone.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelStyleChange} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={handleOverride}
                size="large"
                icon="edit"
                className="override-btn"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={handleClone}
                size="large"
                icon="copy"
              >
                Clone
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Shot Style Change Confirmation Modal */}
      <Modal
        visible={showShotStyleChangeModal}
        onCancel={handleCancelShotStyleChange}
        footer={null}
        centered
        width={520}
        className="video-style-change-modal"
      >
        <div className="video-style-change-modal-content">
          <div className="modal-icon-wrapper">
            <Icon type="warning" className="modal-warning-icon" />
          </div>
          <h3 className="modal-title">Regeneration Required</h3>
          <p className="modal-description">
            You have already generated content for this video. Changing the
            editing style to <strong>"{getPendingShotStyleName()}"</strong> will
            require <strong>all shots to be regenerated</strong> from scratch.
            <br />
            <br />
            Please choose how you would like to proceed:
          </p>
          <div className="modal-options">
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="copy" />
              </div>
              <div className="option-content">
                <h4 className="option-title">
                  Clone as New Video (Recommended)
                </h4>
                <p className="option-description">
                  Safely creates a brand new copy of this video with the new
                  style. Your current video, its shots, and its images will be
                  saved and remain untouched.
                </p>
              </div>
            </div>
            <div className="modal-option">
              <div className="option-icon">
                <Icon type="edit" />
              </div>
              <div className="option-content">
                <h4 className="option-title">Override Current Video</h4>
                <div className="option-description">
                  Updates this exact video.
                  <div
                    style={{
                      border: '1px solid #ffa39e',
                      backgroundColor: '#fff1f0',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      marginTop: '8px',
                      fontSize: '13px',
                      color: 'rgba(0, 0, 0, 0.65)'
                    }}
                  >
                    <Icon
                      type="warning"
                      theme="filled"
                      style={{ color: '#f5222d', marginRight: '6px' }}
                    />
                    <strong>Warning:</strong> All your existing images and shots
                    will be deleted and regenerated to match the new style. This
                    cannot be undone.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <Button onClick={handleCancelShotStyleChange} size="large">
              Cancel
            </Button>

            <div className="modal-actions-right">
              <Button
                type="default"
                onClick={handleShotOverride}
                size="large"
                icon="edit"
                className="override-btn"
              >
                Override
              </Button>
              <Button
                type="primary"
                onClick={handleShotClone}
                size="large"
                icon="copy"
              >
                Clone
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default VideoStyle
